/*
 * Touchpanels: reine Logik für Liste und Verwaltung, ohne Preact, damit sie
 * sich mit node:test prüfen lässt (tests/touchpanelModel.test.ts).
 *
 * Leitlinie (Patrick, 03.11.2026): alle Plattformen verhalten sich gleich.
 * Die Oberfläche kennt deshalb keine Android- oder sinnux-Ansicht, sondern
 * zeigt, was das Panel in seiner Freigabeliste meldet (GET /v1/manage/allowlist,
 * docs/wire-device-management.md 6).
 */

export type TouchpanelPlatform = 'android' | 'sinnux';

/** Zeile aus GET /api/touchpanels (TouchpanelDto auf dem Server). */
export interface TouchpanelDto {
  clientId: string;
  plantId: string;
  plantName: string | null;
  hostname: string;
  platform: TouchpanelPlatform;
  model: 'tp8pro' | 'tp10pro' | null;
  chassis: string | null;
  osPretty: string | null;
  kernel: string | null;
  firmwareBuild: string | null;
  serial: string | null;
  chipSerial: string | null;
  nameplateSerial: string | null;
  appVersion: string | null;
  netPilotVersion: string | null;
  hardening: string | null;
  hardeningEffective: string | null;
  capabilities: string[];
  addresses: { iface: string; ip: string }[];
  online: boolean;
  lastSeenAt: string;
  uptimeSeconds: number | null;
}

export interface ListTouchpanelsResponse {
  touchpanels: TouchpanelDto[];
  truncated: boolean;
}

export function platformLabel(p: TouchpanelPlatform): string {
  return p === 'android' ? 'Android' : 'sinnux';
}

export function modelLabel(m: TouchpanelDto['model']): string {
  if (m === 'tp8pro') return 'TP8 Pro';
  if (m === 'tp10pro') return 'TP10 Pro';
  return 'unbekannt';
}

/** Der Name, der vom Anwender her das Panel ist: der Hostname, sonst die Kennung. */
export function panelLabel(p: Pick<TouchpanelDto, 'hostname' | 'clientId'>): string {
  return p.hostname || p.clientId.slice(0, 8);
}

/** Wie der Client heißt, dessen Fassung in appVersion steht. */
export function appLabel(p: TouchpanelPlatform): string {
  return p === 'android' ? 'OEM-App' : 'Client';
}

export interface TouchpanelFilter {
  text: string;
  platform: TouchpanelPlatform | '';
  model: 'tp8pro' | 'tp10pro' | 'unknown' | '';
  online: 'online' | 'offline' | '';
}

export const EMPTY_FILTER: TouchpanelFilter = { text: '', platform: '', model: '', online: '' };

/** Jedes Wort der Suche muss in einem der Felder vorkommen. */
export function filterTouchpanels(list: TouchpanelDto[], f: TouchpanelFilter): TouchpanelDto[] {
  const words = f.text.toLowerCase().split(/\s+/).filter(Boolean);
  return list.filter((p) => {
    if (f.platform && p.platform !== f.platform) return false;
    if (f.model === 'unknown' ? p.model !== null : f.model && p.model !== f.model) return false;
    if (f.online === 'online' && !p.online) return false;
    if (f.online === 'offline' && p.online) return false;
    if (words.length === 0) return true;
    const hay = [
      p.hostname, p.plantName, p.serial, p.chipSerial, p.nameplateSerial, p.appVersion,
      p.netPilotVersion, p.osPretty, p.firmwareBuild, ...p.addresses.map((a) => a.ip),
    ].filter(Boolean).join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

// ------------------------------------------------------------ Freigabeliste

/** Antwort auf GET /v1/manage/allowlist, beide Clients gleich. */
export interface AllowlistResponse {
  hardeningEffective: string | null;
  entries: { method: string; path: string; group: string; levels?: string }[];
}

/** Die Freigabeliste als Menge "METHODE pfad". `null` heißt: unbekannt. */
export type Allowed = ReadonlySet<string> | null;

export function toAllowed(r: AllowlistResponse | null | undefined): Allowed {
  if (!r || !Array.isArray(r.entries)) return null;
  return new Set(r.entries.map((e) => `${e.method.toUpperCase()} ${e.path}`));
}

/**
 * Nimmt das Panel diesen Pfad an? Ist die Liste unbekannt (älterer Client,
 * Anfrage gescheitert), gilt ja: dann antwortet das Panel selbst mit 403, und
 * die Oberfläche hat keine Funktion versteckt, die es vielleicht doch kann.
 */
export function allows(allowed: Allowed, method: string, path: string): boolean {
  return allowed === null || allowed.has(`${method.toUpperCase()} ${path}`);
}

export type PanelTab = 'updates' | 'anzeige' | 'kiosk' | 'netz' | 'vpn' | 'diagnose';

export const PANEL_TABS: { id: PanelTab; label: string; needs: [string, string][] }[] = [
  { id: 'updates', label: 'Updates', needs: [['GET', '/v1/ota/status']] },
  { id: 'anzeige', label: 'Anzeige', needs: [['GET', '/v1/display/settings']] },
  { id: 'kiosk', label: 'Kiosk und HMI', needs: [['GET', '/v1/hmi']] },
  { id: 'netz', label: 'Netz', needs: [['GET', '/v1/net/ethernet'], ['GET', '/v1/net/wifi']] },
  { id: 'vpn', label: 'VPN', needs: [['GET', '/v1/net/vpn']] },
  {
    id: 'diagnose', label: 'Diagnose',
    needs: [['POST', '/v1/system/reboot'], ['GET', '/v1/logs/journal'], ['GET', '/v1/logs/logcat']],
  },
];

/** Reiter, von denen das Panel mindestens einen Pfad annimmt. */
export function visibleTabs(allowed: Allowed): typeof PANEL_TABS {
  return PANEL_TABS.filter((t) => t.needs.some(([m, p]) => allows(allowed, m, p)));
}

/** Pfad für das Systemprotokoll: sinnux hat das Journal, Android logcat. */
export function systemLogPath(allowed: Allowed, platform: TouchpanelPlatform): string {
  if (allowed === null) return platform === 'sinnux' ? '/v1/logs/journal' : '/v1/logs/logcat';
  return allowed.has('GET /v1/logs/journal') ? '/v1/logs/journal' : '/v1/logs/logcat';
}
